import { useQuery } from "@tanstack/react-query";
import {
  AccessModule,
  ApiPermission
} from "@shoutout-labs/market_buzz_crm_types";
import PermissionsService from "../services/PermissionsService";
import { useAuthStore } from "../store/useAuthStore";

export interface UsePermissionsOptions {
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
  retry?: number;
}

/**
 * Custom hook for managing user permissions with React Query
 * Provides easy access to user's module and API permissions
 */
export const usePermissions = (options: UsePermissionsOptions = {}) => {
  const {
    enabled = true,
    staleTime = 5 * 60 * 1000, // 5 minutes
    gcTime = 10 * 60 * 1000, // 10 minutes
    retry = 2
  } = options;

  // Get subscription information from JWT token
  const subscription = useAuthStore((state) => state.subscription);
  const appInfo = useAuthStore((state) => state.appInfo);

  const query = useQuery({
    queryKey: ["user-permissions"],
    queryFn: PermissionsService.getCurrentUserPermissions,
    staleTime,
    gcTime,
    retry,
    enabled
  });

  const { data: permissions, isLoading, error, refetch } = query;

  /**
   * Check if user has access to a specific module
   */
  const hasModuleAccess = (module: AccessModule): boolean => {
    return permissions?.modules.includes(module) ?? false;
  };

  /**
   * Check if user has a specific API permission
   */
  const hasApiPermission = (permission: ApiPermission): boolean => {
    return permissions?.apiPermissions.includes(permission) ?? false;
  };

  /**
   * Check if user has access to multiple modules (any of them)
   */
  const hasAnyModuleAccess = (modules: AccessModule[]): boolean => {
    if (!permissions) return false;
    return modules.some((module) => permissions.modules.includes(module));
  };

  /**
   * Check if user has all specified module access
   */
  const hasAllModulesAccess = (modules: AccessModule[]): boolean => {
    if (!permissions) return false;
    return modules.every((module) => permissions.modules.includes(module));
  };

  /**
   * Check if user has any of the specified API permissions
   */
  const hasAnyApiPermission = (perms: ApiPermission[]): boolean => {
    if (!permissions) return false;
    return perms.some((permission) =>
      permissions.apiPermissions.includes(permission)
    );
  };

  /**
   * Check if user has all specified API permissions
   */
  const hasAllApiPermissions = (perms: ApiPermission[]): boolean => {
    if (!permissions) return false;
    return perms.every((permission) =>
      permissions.apiPermissions.includes(permission)
    );
  };

  /**
   * Check if user has a specific feature
   */
  const hasFeature = (feature: string): boolean => {
    return permissions?.features?.includes(feature) ?? false;
  };

  /**
   * Check if user has any of the specified features
   */
  const hasAnyFeature = (features: string[]): boolean => {
    if (!permissions?.features) return false;
    return features.some((feature) => permissions.features!.includes(feature));
  };

  return {
    // Raw data and loading states
    permissions,
    isLoading,
    error,
    refetch,

    // JWT subscription information
    subscription,
    appInfo,

    // Convenience methods
    hasModuleAccess,
    hasApiPermission,
    hasAnyModuleAccess,
    hasAllModulesAccess,
    hasAnyApiPermission,
    hasAllApiPermissions,
    hasFeature,
    hasAnyFeature,

    // Direct access to arrays
    modules: permissions?.modules ?? [],
    apiPermissions: permissions?.apiPermissions ?? [],
    features: permissions?.features ?? [],

    // Meta information
    meta: permissions?.meta,

    // Status booleans
    isReady: !isLoading && !error,
    hasError: !!error,
    hasPermissions: !!permissions
  };
};

export default usePermissions;
